import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import studentTracking from '../utils/studentTracking';

// Custom hook for student activity tracking
export const useStudentTracking = () => {
  const { user, token } = useAuth();
  const pageStartTime = useRef(null);
  const currentPage = useRef(null);

  // Initialize tracking when user changes
  useEffect(() => {
    if (user && token) {
      studentTracking.initialize(user, token);
    }
  }, [user, token]);

  // Track page view when component mounts
  const trackPageView = (pageName, additionalData = {}) => {
    if (currentPage.current !== pageName) {
      // Track time spent on previous page
      if (currentPage.current && pageStartTime.current) {
        const timeSpent = Date.now() - pageStartTime.current;
        studentTracking.trackTimeOnPage(currentPage.current, timeSpent);
      }
      
      // Start tracking new page
      currentPage.current = pageName;
      pageStartTime.current = Date.now();
      studentTracking.trackPageView(pageName, additionalData);
    }
  };

  // Track form interactions
  const trackFormInteraction = (formName, action, fieldName = null, value = null) => {
    studentTracking.trackFormInteraction(formName, action, fieldName, value);
  };

  // Track button clicks
  const trackButtonClick = (buttonName, context = {}) => {
    studentTracking.trackButtonClick(buttonName, context);
  };

  // Track data operations
  const trackDataOperation = (operation, entityType, entityId = null, details = {}) => {
    studentTracking.trackDataOperation(operation, entityType, entityId, details);
  };

  // Track search activities
  const trackSearch = (searchTerm, resultsCount = null, filters = {}) => {
    studentTracking.trackSearch(searchTerm, resultsCount, filters);
  };

  // Track file operations
  const trackFileOperation = (operation, fileType, fileName = null, fileSize = null) => {
    studentTracking.trackFileOperation(operation, fileType, fileName, fileSize);
  };

  // Track navigation
  const trackNavigation = (fromPage, toPage, method = 'click') => {
    studentTracking.trackNavigation(fromPage, toPage, method);
  };

  // Track errors
  const trackError = (errorType, errorMessage, context = {}) => {
    studentTracking.trackError(errorType, errorMessage, context);
  };

  // Dean-focused helpers
  const trackRecordAccess = (entityType, entityId, purpose = 'view', context = {}) => {
    studentTracking.trackRecordAccess(entityType, entityId, purpose, context);
  };
  const trackAuthEvent = (eventType, context = {}) => {
    studentTracking.trackAuthEvent(eventType, context);
  };
  const trackSecurityEvent = (eventType, context = {}) => {
    studentTracking.trackSecurityEvent(eventType, context);
  };

  // Track modal interactions
  const trackModalInteraction = (modalName, action, context = {}) => {
    studentTracking.trackModalInteraction(modalName, action, context);
  };

  // Track API calls
  const trackApiCall = (endpoint, method, statusCode, responseTime = null) => {
    studentTracking.trackApiCall(endpoint, method, statusCode, responseTime);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentPage.current && pageStartTime.current) {
        const timeSpent = Date.now() - pageStartTime.current;
        studentTracking.trackTimeOnPage(currentPage.current, timeSpent);
      }
    };
  }, []);

  return {
    trackPageView,
    trackFormInteraction,
    trackButtonClick,
    trackDataOperation,
    trackSearch,
    trackFileOperation,
    trackNavigation,
    trackError,
    trackModalInteraction,
    trackApiCall,
    trackRecordAccess,
    trackAuthEvent,
    trackSecurityEvent
  };
};

// Higher-order component for automatic page tracking
export const withStudentTracking = (WrappedComponent, pageName) => {
  return function TrackedComponent(props) {
    const { trackPageView } = useStudentTracking();

    useEffect(() => {
      trackPageView(pageName);
    }, [trackPageView]);

    return <WrappedComponent {...props} />;
  };
};

export default useStudentTracking;

